import { Box, Flex, type FlexProps, IconButton, type IconButtonProps } from "@chakra-ui/react"
import { Fragment } from "react"

type Props = {
  // Search field (usually <Searchbar />): takes the remaining width.
  search?: React.ReactNode
  // Advanced filters button.
  filters?: React.ReactNode
  // Page actions (create, export…).
  actions?: React.ReactNode
  // Result layout toggles (<ToolbarIconButton />), last so the bar ends with display options.
  views?: React.ReactNode
} & Omit<FlexProps, "children">

const isPresent = (node: React.ReactNode) => node !== undefined && node !== null && node !== false

// Shared list toolbar (ADR 0003, D4): one 40px height (controlHSm, ADR 0001 Q7), one gap, and a
// vertical divider between the groups that are present. The search group grows.
export const ListToolbar: React.FC<Props> = ({ search, filters, actions, views, ...flexProps }) => {
  const groups = [
    { key: "search", node: search, grow: true },
    { key: "filters", node: filters, grow: false },
    { key: "actions", node: actions, grow: false },
    { key: "views", node: views, grow: false },
  ].filter(group => isPresent(group.node))

  return (
    <Flex
      alignItems="stretch"
      gap="4"
      minH="controlHSm"
      wrap={{ base: "wrap", lg: "nowrap" }}
      {...flexProps}
    >
      {groups.map((group, index) => (
        <Fragment key={group.key}>
          {index > 0 && (
            <Box w="1px" flexShrink={0} bg="line" display={{ base: "none", lg: "block" }} />
          )}
          <Flex
            alignItems="center"
            gap="2"
            wrap={{ base: "wrap", lg: "nowrap" }}
            flexGrow={group.grow ? 1 : 0}
            flexShrink={group.grow ? 1 : 0}
            // Below lg the search takes a row of its own and the other groups wrap under it,
            // without dividers. The layout toggles are hidden: lists are always cards there.
            flexBasis={group.grow ? { base: "100%", lg: "auto" } : "auto"}
            display={group.key === "views" ? { base: "none", lg: "flex" } : "flex"}
            minW={0}
            data-testid={`list-toolbar-${group.key}`}
          >
            {group.node}
          </Flex>
        </Fragment>
      ))}
    </Flex>
  )
}

type ToolbarIconButtonProps = {
  isActive?: boolean
  // An outline button stands for a text button reduced to its icon (advanced filters).
  variant?: "outline"
} & Omit<IconButtonProps, "size" | "variant">

// 40px square toolbar button; the active layout toggle is solid black.
export const ToolbarIconButton: React.FC<ToolbarIconButtonProps> = ({
  isActive,
  variant,
  ...props
}) => (
  <IconButton
    size="toolbar"
    boxSize="controlHSm"
    minW="controlHSm"
    px="0"
    variant={variant ?? (isActive ? "solid" : "transparent")}
    flexShrink={0}
    {...props}
  />
)
